import classNames from "classnames";
import * as React from "react";
import { useSearchParams } from "react-router-dom";

import { useEphemeralWorkspaceStateLoading } from "../WorkspaceCtx";

import { KeywordFilter } from "./filter";
import { FilterInput } from "./FilterInput";
import styles from "./Topics.module.css";
import { TopicTree, TopicTreeSkeleton } from "./TopicTree";

interface TopicsProps {
  fileId: string | undefined;
  className?: classNames.Argument;
  header?: React.ReactNode;
}

export function Topics(props: TopicsProps) {
  const { fileId, header } = props;

  const [searchParams, setSearchParams] = useSearchParams();

  const sp_filterTerm = searchParams.get("filterTerm");
  const initialFilterTerm = sp_filterTerm ?? "";
  const [filter, setFilter] = React.useState<KeywordFilter>({
    type: "keyword",
    term: initialFilterTerm,
  });

  // Sync filter state when searchParams change externally
  React.useEffect(() => {
    const currentFilterTerm = searchParams.get("filterTerm") ?? "";
    if (currentFilterTerm !== filter.term) {
      setFilter({
        type: "keyword",
        term: currentFilterTerm,
      });
    }
  }, [searchParams, filter.term]);

  const isDataLoading = useEphemeralWorkspaceStateLoading();

  const handleFilterChange = React.useCallback(
    (filter: KeywordFilter) => {
      setFilter(filter);
      setSearchParams(
        (params) => {
          if (filter.term) {
            params.set("filterTerm", filter.term);
          } else {
            params.delete("filterTerm");
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <div className={classNames(props.className, styles.container)}>
      {header}
      <FilterInput
        key={filter.term}
        className={styles.filter}
        onFilterChange={handleFilterChange}
        initialValue={filter.term}
      />
      {isDataLoading ? (
        <TopicTreeSkeleton estimatedTopicCount={15} />
      ) : (
        <TopicTree fileId={fileId} filter={filter} />
      )}
    </div>
  );
}
